import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { wordService } from '../services/wordService';
import { trainingService } from '../services/trainingService';
import { teamService } from '../services/teamService';
import { statsService } from '../services/statsService';
import { WordDisplay } from '../components/game/WordDisplay';
import { OutcomePanel } from '../components/game/OutcomePanel';
import confetti from 'canvas-confetti';
import './TrainingGame.css';

const TrainingGame = () => {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const teamId = params.get('teamId') || location.state?.teamId;
  const myRole = params.get('role') || location.state?.role;

  const [gameState, setGameState] = useState(null);
  const [countdown, setCountdown] = useState(3);
  const [startTime, setStartTime] = useState(0);
  const [now, setNow] = useState(0);
  const [savedStats, setSavedStats] = useState(false);
  const unsubRef = useRef(null);
  const countdownStartedRef = useRef(false);
  const wasFinishedRef = useRef(false);
  const amIHost = useRef(false);

  const isGiver = myRole === 'speaker1' || myRole === 'speaker2';
  const isGuesser = myRole === 'guesser';

  // Subscribe to Firestore game state
  useEffect(() => {
    if (!teamId) { navigate('/training'); return; }
    unsubRef.current = teamService.subscribeToTeam(teamId, (data) => {
      const gs = data?.trainingSession?.gameState;
      if (!gs) {
        navigate('/training');
        return;
      }
      if (gs.startedBy === currentUser?.uid) amIHost.current = true;

      setGameState(gs);

      if (gs.status === 'finished' && !wasFinishedRef.current) {
        wasFinishedRef.current = true;
        confetti({ particleCount: 100, spread: 80, origin: { y: 0.6 }, colors: ['#FFCC00', '#22C55E'] });
      }
    });
    return () => { if (unsubRef.current) unsubRef.current(); };
  }, [teamId, currentUser, navigate]);

  // Countdown: local UI driven by countdownStartedAt
  useEffect(() => {
    if (gameState?.status !== 'countdown') { countdownStartedRef.current = false; return; }
    if (countdownStartedRef.current) return;
    countdownStartedRef.current = true;

    const startedAt = gameState.countdownStartedAt || Date.now();
    const elapsed = Date.now() - startedAt;
    const startVal = Math.max(1, 3 - Math.floor(elapsed / 900));
    setCountdown(startVal);

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          if (amIHost.current) {
            teamService.updateTrainingGameState(teamId, {
              status: 'playing',
              timerRunning: true,
              timerStartedAt: Date.now(),
              pausedAt: null,
            }).catch(console.error);
          }
          return prev;
        }
        return prev - 1;
      });
    }, 900);

    return () => clearInterval(interval);
  }, [gameState?.status, gameState?.countdownStartedAt, teamId]);

  // Stopwatch (local, runs during 'playing')
  useEffect(() => {
    let iv;
    if (gameState?.status === 'playing') {
      const t = Date.now();
      setStartTime(t);
      setNow(t);
      iv = setInterval(() => setNow(Date.now()), 100);
    } else {
      setStartTime(0);
      setNow(0);
    }
    return () => clearInterval(iv);
  }, [gameState?.status]);

  // Save individual progress when finished
  useEffect(() => {
    if (gameState?.status !== 'finished' || savedStats || !currentUser) return;
    setSavedStats(true);

    statsService.saveGameResult(currentUser.uid, {
      score: gameState.score || 0,
      wordsGuessed: gameState.wordsGuessed || 0,
      wrongAnswers: gameState.wrongAnswers || 0,
      mode: 'training',
    }).catch(console.error);
  }, [gameState?.status, savedStats, currentUser, gameState]);

  const elapsed = startTime ? (now - startTime) / 1000 : 0;

  // ── Actions ───────────────────────────────────────────────

  const handleStop = useCallback(async () => {
    if (!gameState || gameState.status !== 'playing') return;
    try {
      await teamService.updateTrainingGameState(teamId, {
        status: 'paused',
        pausedAt: Date.now(),
        wordElapsed: elapsed,
      });
    } catch (e) {
      console.error('[TG] handleStop error:', e);
    }
  }, [teamId, gameState, elapsed]);

  const handleOutcome = useCallback(async (type) => {
    if (!gameState) return;
    const wordElapsed = gameState.wordElapsed || 0;
    const currentWordObj = gameState.words?.[gameState.currentWordIndex];
    const cleanWord = currentWordObj?.text?.trim() || '';
    const wordId = currentWordObj?.id;

    if (wordId) wordService.updateWordStats(wordId, type === 'correct' ? 'correct' : 'wrong', wordElapsed);
    // Save to team-scoped training progress (shared by all members)
    if (teamId && cleanWord) {
      trainingService.saveWordResult(teamId, cleanWord, wordElapsed, type === 'correct').catch(console.error);
    }

    const next = (gameState.currentWordIndex || 0) + 1;
    const isFinished = next >= (gameState.words?.length || 0);

    await teamService.updateTrainingGameState(teamId, {
      currentWordIndex: next,
      score: type === 'correct' ? (gameState.score || 0) + 1 : Math.max(0, (gameState.score || 0) - 1),
      wordsGuessed: type === 'correct' ? (gameState.wordsGuessed || 0) + 1 : (gameState.wordsGuessed || 0),
      wrongAnswers: type === 'wrong' ? (gameState.wrongAnswers || 0) + 1 : (gameState.wrongAnswers || 0),
      wordElapsed: 0,
      status: isFinished ? 'finished' : 'playing',
      pausedAt: null,
    }).catch((e) => console.error('[TG] handleOutcome error:', e));
  }, [teamId, gameState, currentUser]);

  const handleExit = async () => {
    if (gameState?.status !== 'finished') {
      if (!window.confirm("Vuoi davvero uscire dall'allenamento?")) return;
    }
    if (amIHost.current) await teamService.clearTrainingGame(teamId).catch(console.error);
    navigate('/training');
  };

  const handleRestart = async () => {
    if (!amIHost.current) return;
    wasFinishedRef.current = false;
    setSavedStats(false);
    const shuffled = [...(gameState.words || [])].sort(() => Math.random() - 0.5);
    await teamService.startTrainingGame(teamId, shuffled, currentUser.uid).catch(console.error);
  };

  // ── Loading ───────────────────────────────────────────────
  if (!gameState) {
    return <div className="tg-loading"><p>Connessione alla sessione…</p></div>;
  }

  const currentWord = gameState.words?.[gameState.currentWordIndex];
  const totalWords = gameState.words?.length || 0;
  const sessionProgressPct = totalWords > 0 ? ((gameState.wordsGuessed || 0) / totalWords) * 100 : 0;

  // ── Finished ──────────────────────────────────────────────
  if (gameState.status === 'finished') {
    return (
      <div className="tg-page">
        <div className="tg-end u-scale-in">
          <h1 className="tg-end__title">Allenamento Completato!</h1>
          <div className="tg-end__score">
            <span className="tg-end__val">{gameState.score || 0}</span>
            <span className="tg-end__lbl">PUNTI</span>
          </div>
          <div className="tg-end__stats">
            <span>{gameState.wordsGuessed || 0} / {totalWords} parole corrette</span>
          </div>
          <div className="tg-end__btns">
            {amIHost.current && (
              <button className="start-btn" onClick={handleRestart}>Ricomincia</button>
            )}
            <button className="back-link" onClick={handleExit}>← Esci</button>
          </div>
          {!amIHost.current && (
            <p className="tg-end__waiting">In attesa che l'host riavvii…</p>
          )}
        </div>
      </div>
    );
  }

  // ── Active Game ───────────────────────────────────────────
  return (
    <div className="tg-page">
      <div className="tg-topbar">
        <div className="tg-topbar__left">
          <button className="hud-exit" onClick={handleExit} id="btn-tg-exit">ESCI</button>
        </div>
        <div className="tg-topbar__center">
          <span className="tg-topbar__info">
            {gameState.wordsGuessed || 0}/{totalWords} parole
            {' · '}
            {myRole === 'speaker1' ? '🗣️ Speaker 1' : myRole === 'speaker2' ? '🎙️ Speaker 2' : '🎯 Guesser'}
          </span>
        </div>
        <div className="tg-topbar__right">
          <div className="tg-stopwatch">
            <span className="tg-stopwatch__icon">⏱</span>
            <span className="tg-stopwatch__val">{elapsed.toFixed(1)}s</span>
          </div>
        </div>
      </div>

      <div className="tg-progress">
        <div className="tg-progress__fill" style={{ width: `${sessionProgressPct}%` }} />
      </div>

      <div className="tg-main">
        {/* COUNTDOWN */}
        {gameState.status === 'countdown' && (
          <div className="game-overlay">
            <span className="countdown-number" key={countdown}>{countdown}</span>
          </div>
        )}

        {/* PLAYING */}
        {gameState.status === 'playing' && (
          <>
            {isGiver && currentWord && (
              <WordDisplay word={currentWord.text} animKey={gameState.currentWordIndex} />
            )}
            {isGuesser && (
              <div className="tg-guesser-container">
                <button className="guesser-stop-btn" onClick={handleStop} id="btn-tg-stop">
                  STOP
                </button>
              </div>
            )}
          </>
        )}

        {/* PAUSED — outcome (only givers decide) */}
        {gameState.status === 'paused' && (
          <>
            {isGiver && currentWord && (
              <OutcomePanel
                word={currentWord.text}
                onCorrect={() => handleOutcome('correct')}
                onWrong={() => handleOutcome('wrong')}
                showPass={false}
              />
            )}
            {isGuesser && (
              <div className="mp-waiting">
                <div className="mp-waiting__indicator">
                  <div className="pulse-circle"></div>
                  <div className="pulse-circle"></div>
                </div>
                <p>I suggeritori stanno valutando…</p>
                <span className="mp-waiting__sub">Attendi la prossima parola</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TrainingGame;
